import { env } from './env'
import { getActiveWorkspaceId, getSelectedWorkspaceId } from './api/workspace-context'
import { getAuthItem } from './authStorage'

const EMAIL_INTEGRATION_PATH = 'integrations/email'

export function emailApiBase(): string {
  return `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/${EMAIL_INTEGRATION_PATH}`
}

export function emailApiPath(): string {
  return `/workspaces/${getActiveWorkspaceId()}/${EMAIL_INTEGRATION_PATH}`
}

export function getHeaders(): Record<string, string> {
  const token = getAuthItem('xavior_auth_token');
  const userId = getAuthItem('userId');
  // #2736 — workspace-scoped routes (gmail/status, search, drafts, etc.)
  // require X-Workspace-Id or workspace context in the JWT. Send the
  // header to match apps/web/src/lib/apiClient.ts's canonical pattern.
  const workspaceId = getSelectedWorkspaceId();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
    ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
  };
}

/** Decode a base64 / base64url string into a typed Blob. */
export function base64ToBlob(data: string, mimeType: string): Blob {
  const normalized = data.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/**
 * Fetch an email attachment and return it as a decoded Blob.
 *
 * The `/attachment` endpoint returns a JSON envelope
 * `{ data: <base64url>, mimeType, filename, size }` (not raw bytes), so every
 * consumer (thumbnail, download, inline preview) must base64-decode it. This is
 * the single place that knows that contract. Falls back to `res.blob()` if the
 * server ever returns raw bytes instead of JSON.
 */
export async function fetchAttachmentBlob(params: {
  messageId: string;
  attachmentId: string;
  filename?: string;
  mimeType?: string;
  accountId?: string;
}): Promise<Blob> {
  const res = await fetch(`${emailApiBase()}/attachment`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      messageId: params.messageId,
      attachmentId: params.attachmentId,
      ...(params.filename ? { filename: params.filename } : {}),
      ...(params.mimeType ? { mimeType: params.mimeType } : {}),
      ...(params.accountId ? { accountId: params.accountId } : {}),
    }),
  });
  if (!res.ok) throw new Error(`Attachment fetch failed: ${res.status}`);
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const json = (await res.json()) as { data?: string; mimeType?: string };
    if (!json.data) throw new Error('Attachment response had no data');
    return base64ToBlob(json.data, json.mimeType || params.mimeType || 'application/octet-stream');
  }
  return res.blob();
}
